import { FormEvent, useState, ChangeEvent, useEffect, useMemo } from 'react';
import intl from 'react-intl-universal';
import isEmpty from 'lodash/isEmpty';
import {
  Modal,
  Typography,
  FormItem,
  TextField,
  Dropdown,
  TextArea,
  UsersPicker,
} from '@getsynapse/design-system';
import { CREATE_TEAM_FORM, SHORT_INPUTS_LENGTH } from 'utils/constants';
import { FormOption, Option, UserAvatars, UserOption } from 'utils/customTypes';
import type { LearningTeamForm } from 'utils/types/learningTeam';
import UnsavedChangesModal from 'Organisms/UnsavedChangesModal/UnsavedChangesModal';

type NewLDTeamModalProps = {
  isOpen?: boolean;
  setIsOpen: (value: boolean) => void;
  ldOptions: FormOption[];
  ldUsers: UserAvatars[];
  onAddTeam: (team: LearningTeamForm) => void;
};

type NewLDTeamForm = LearningTeamForm & {
  teamManagers?: any[];
};

const NewLDTeamModal = ({
  isOpen = false,
  setIsOpen,
  ldOptions,
  ldUsers,
  onAddTeam,
}: NewLDTeamModalProps) => {
  const initialState: LearningTeamForm = useMemo(
    () => ({
      name: '',
      description: '',
      parent_id: null,
    }),
    []
  );
  const [newTeam, setNewTeam] = useState<NewLDTeamForm>(initialState);

  const [hasChanges, setHasChanges] = useState(false);
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();
    onAddTeam(newTeam);
    handleClose();
  };

  const handleClose = () => {
    setNewTeam(initialState);
    setIsOpen(false);
    setHasChanges(false);
  };

  useEffect(() => {
    const teamManagers = newTeam.teamManagers || [];
    if (
      newTeam.name !== initialState.name ||
      newTeam.description !== initialState.description ||
      newTeam.parent_id !== initialState.parent_id ||
      teamManagers.length > 0
    ) {
      setHasChanges(true);
    } else {
      setHasChanges(false);
    }
  }, [newTeam, initialState]);

  return (
    <Modal
      isOpen={isOpen}
      closeModal={() => {
        if (hasChanges) {
          setShowUnsavedChangesModal(true);
        } else {
          setShowUnsavedChangesModal(false);
          handleClose();
        }
      }}
      aria-label='add-ld-team'
      data-testid='add-ld-team_modal'
      title={intl.get('ORG_SETTINGS_PAGE.LEARNING_TEAMS.ADD_TEAM')}
      size='large'
      actionButtons={[
        {
          children: intl.get('SAVE'),
          variant: 'primary',
          'data-cy': 'confirm-button',
          type: 'submit',
          form: CREATE_TEAM_FORM,
          disabled: isEmpty(newTeam.name),
        },
        {
          children: intl.get('CANCEL'),
          variant: 'tertiary',
          onClick: handleClose,
          'data-cy': 'cancel-button',
        },
      ]}
    >
      <Typography>
        {intl.get(
          'ORG_SETTINGS_PAGE.LEARNING_TEAMS.ADD_TEAM_MODAL.FILL_DETAILS'
        )}
      </Typography>
      <form
        id={CREATE_TEAM_FORM}
        className='grid grid-cols-2 gap-x-9 mt-4'
        onSubmit={handleSubmit}
      >
        <FormItem className='h-16'>
          <TextField
            labelProps={{ required: true }}
            label={intl.get(
              'ORG_SETTINGS_PAGE.LEARNING_TEAMS.ADD_TEAM_MODAL.TEAM_NAME'
            )}
            data-testid='team-name_input'
            className='h-10'
            placeholder={intl.get(
              'ORG_SETTINGS_PAGE.LEARNING_TEAMS.ADD_TEAM_MODAL.ENTER_TEAM_NAME'
            )}
            value={newTeam.name}
            displayCharactersCount
            maxLength={SHORT_INPUTS_LENGTH}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              setNewTeam((prev) => ({ ...prev, name: event.target.value }))
            }
          />
        </FormItem>
        <FormItem
          className='row-span-2'
          label={intl.get('DESCRIPTION', { num: 1 })}
        >
          <TextArea
            value={newTeam.description}
            onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
              setNewTeam((prev) => ({
                ...prev,
                description: event.target.value,
              }))
            }
            textAreaProps={{
              'data-testid': 'team-description_input',
              className: 'h-30',
              placeholder: intl.get(
                'ORG_SETTINGS_PAGE.LEARNING_TEAMS.ADD_TEAM_MODAL.ENTER_DESCRIPTION'
              ),
            }}
          />
        </FormItem>
        <FormItem
          className='h-16'
          label={intl.get(
            'ORG_SETTINGS_PAGE.LEARNING_TEAMS.ADD_TEAM_MODAL.REPORTS_TO'
          )}
        >
          <Dropdown
            onChange={(option: Option) =>
              setNewTeam((prev) => ({ ...prev, parent_id: option.value }))
            }
            options={ldOptions}
            values={[]}
            triggerProps={{
              'data-testid': 'team-parent_dropdown',
              className: 'h-10',
              placeholder: intl.get(
                'ORG_SETTINGS_PAGE.LEARNING_TEAMS.ADD_TEAM_MODAL.SELECT_TEAM'
              ),
            }}
          />
        </FormItem>
        <FormItem label={intl.get('ENTITIES.TEAM_MANAGER')}>
          <UsersPicker
            deactivatedUserText={intl.get('DEACTIVATED')}
            usersList={ldUsers}
            selectedUsersList={[]}
            onChange={(users: UserOption[]) =>
              setNewTeam((prev) => ({
                ...prev,
                teamManagers: users.map((user) => user.value),
              }))
            }
            triggerText={intl.get(
              'ORG_SETTINGS_PAGE.LEARNING_TEAMS.ADD_TEAM_MODAL.ADD_MANAGER'
            )}
            triggerProps={{
              type: 'button',
              'data-testid': 'team-manager_dropdown',
            }}
          />
        </FormItem>
      </form>
      <UnsavedChangesModal
        isOpen={showUnsavedChangesModal}
        setIsOpen={setShowUnsavedChangesModal}
        onConfirm={() => handleClose()}
      />
    </Modal>
  );
};

export default NewLDTeamModal;
